import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CpRolesEnum, UI_DATE_FORMAT_MAP } from 'appRootPath/shared/global-meta-data';
import { groupBy } from 'appRootPath/utils';
import { Observable, Subject } from 'rxjs';

import { BidThread, IssueFromAuctionStateConf, StateObject, ViewModalDocFormat } from '../models/commercial-papers.model';

import {
  BID_ACTIONS,
  ACTOR_STATES,
  INVESTOR_CURRENT_STATE_CONFIG,
  INVESTOR_NEXT_ACTIONS_CONFIG,
  INVESTOR_STATE_NEXT_ACTIONS,
  ISSUER_CURRENT_STATE_CONFIG,
  ISSUER_NEXT_ACTIONS_CONFIG,
  ISSUER_STATE_NEXT_ACTIONS,
  BID_STATES_FROM_ACTOR_STATES,
  BID_STATES
} from '../shared/auction-metadata';
import { CpAuctionApiService } from './cp-auction-api.service';
import { SharedDataService } from './shared-data.service';
import * as moment from 'moment';

const findLatestBid = (allBids: BidThread[], transitions): BidThread => {
  const latestBid = allBids.find((bid) => transitions.includes(bid.bid.state_value));

  return latestBid;
};

const filterBidsByTransition = (allBidsThreads: BidThread[], transitions): BidThread[] => {
  const filteredBid = allBidsThreads.filter((bidThread) => transitions.includes(bidThread.bid.state_value));

  return filteredBid;
};

const isThreadAtEndState = (latestBid: BidThread): boolean => {
  const endStates = [
    ACTOR_STATES.AUCTION_CLOSED,
    ACTOR_STATES.BID_REJECTED,
    ACTOR_STATES.WAITING_FOR_ISSUER_TO_CLOSE_AUCTION
  ];
  return endStates.includes(latestBid.issuer_state.state);
};

@Injectable({
  providedIn: 'root'
})
export class AuctionService {

  roleBiddingConfig;

  issuefromAuctionConf: IssueFromAuctionStateConf = {
    cpFormStatus: 'INVALID',
    issueFormStatus: 'INVALID',
    investorFormStatus: 'INVALID',
    isinFormStatus: 'INVALID'
  };

  /**
   * When isin, cp or issue form validity changes in create issue from auction component,
   * enable/disable submit button in investment details form (pre-existing-issue-subform).
   */
  createIssueSubject = new Subject<IssueFromAuctionStateConf>();
  createIssueObservable = <Observable<IssueFromAuctionStateConf>>this.createIssueSubject.asObservable();

  /**
   * when auction is closed in accepted bids component, refresh auction data in price discovery for issuer
   */
  refreshAuctionSubject = new Subject<string>();
  refreshAuctionObservable = <Observable<string>>this.refreshAuctionSubject.asObservable();


  constructor(
    private sharedDataService: SharedDataService,
    private auctionApiService: CpAuctionApiService,
    private router: Router
  ) {
    this.roleBiddingConfig = this.getConfigForCurrentRole();

    this.createIssueObservable.subscribe((issueConfig: IssueFromAuctionStateConf) => {
      this.issuefromAuctionConf = issueConfig;
    });

  }

  /**
   * Handle close auction API and related changes
   * @param auctionId auction Id to close
   */
  closeAuction(auctionId) {
    return this.auctionApiService.closeAuction(auctionId);
  }

  /**
   * Handle create issue from auction and related changes if any
   * Navigates to create issue from auction page.
   * @param auctionId  auction Id to create issue from
   */
  createIssueFromAuction(auctionId) {
    this.router.navigate([`/issuer/new-issue/auction/${auctionId}`]);
  }

  /**
   * add thread data to each bid
   * @param allBidsOfCurrentThread bids array
   * @param threadData all thread data
   */
  addThreadDataToBids(threadData, bidState) {
    // creating threadDataWithoutBids which contains all thread data but omits the thread's bid array
    const { bid, ...threadDataWithoutBids } = threadData;
    if (!bid) {
      return [];
    }
    return bid.map(bidObj => ({
      bid: bidObj,
      bidState,
      ...threadDataWithoutBids
    }));
  }

  /**
   * Returns the thread object with thread Id as key and thread details as value
   * Returns objects in a format that enables UI view to access data easily.
   * @param bidThread the list of all bids for the given auction
   */
  processBidThread(bidThread: BidThread[]) {
    const threadIdBidsMap = groupBy(bidThread, 'id');
    const threads = {};
    let bidCount = 0;
    Object.keys(threadIdBidsMap).forEach((threadId) => {
      const threadData: BidThread = threadIdBidsMap[threadId][0];
      const isFinalState = ['accepted', 'rejected', 'expired'].includes(threadData.status);
      const issuer_state = threadData.issuer_state;
      const investor_state = threadData.investor_state;
      let lastBid; let lastCounter = null;
      let declinedBids; let acceptedBids = [];
      const bidState = this.getBidState(issuer_state, investor_state);
      const allBidsOfCurrentThread = this.addThreadDataToBids(threadData, bidState);


      bidCount += allBidsOfCurrentThread.length;

      if (isFinalState) {
        acceptedBids = filterBidsByTransition(allBidsOfCurrentThread, [
          BID_ACTIONS.ACCEPT_BID,
          BID_ACTIONS.ACCEPT_NEGOTIATION,
          BID_ACTIONS.ACCEPT_REEVAL_CBC
        ]);

        declinedBids = filterBidsByTransition(allBidsOfCurrentThread, [
          BID_ACTIONS.REJECT_BID,
          BID_ACTIONS.REJECT_NEGOTIATION,
          BID_ACTIONS.REJECT_REEVAL_CBC
        ]);

      } else {
        const counters =
          allBidsOfCurrentThread.filter(bidData => bidData.bid.created_by_role_type === CpRolesEnum.ISSUER);
        const bids =
          allBidsOfCurrentThread.filter(bidData => bidData.bid.created_by_role_type === CpRolesEnum.INVESTOR);

        lastCounter = findLatestBid(counters, [
          BID_ACTIONS.ACCEPT_BID,
          BID_ACTIONS.COUNTER_BID,
          BID_ACTIONS.COUNTER_NEGOTIATION,
          BID_ACTIONS.ACCEPT_NEGOTIATION,
          BID_ACTIONS.ACCEPT_NEGOTIATION_WITH_CBC
        ]);

        lastBid = findLatestBid(bids, [
          BID_ACTIONS.COUNTER_BID,
          BID_ACTIONS.PLACE_BID,
          BID_ACTIONS.COUNTER_NEGOTIATION,
          BID_ACTIONS.ACCEPT_BID,
          BID_ACTIONS.ACCEPT_BID_WITH_CBC
        ]);
      }

      threads[threadId] = {
        allBids: allBidsOfCurrentThread,
        isFinalState,
        bidState,
        issuer_state,
        investor_state,
        lastBid: lastBid || {},
        lastCounter: lastCounter || {},
        acceptedBids: acceptedBids || [],
        declinedBids: declinedBids || [],
      };
    });
    return {
      threads,
      bidCount
    };
  }

  /**
   * returns bid state from issuer and investor states.
   * @param issuer_state the issuer state of bid thread
   * @param investor_state the investor state of bid thread
   * @returns bid state
   */
  getBidState(isss: StateObject, invs: StateObject) {
    const baseBidState = BID_STATES_FROM_ACTOR_STATES[isss.state][invs.state];
    let transitionalBidState = null;
    switch (baseBidState) {
      case BID_STATES.INVESTOR_AFTER_ISSUER_COUNTERS_BID:
        if (isss.state_value === BID_ACTIONS.REJECT_NEGOTIATION &&
          invs.state_value === BID_ACTIONS.ACCEPT_BID) {
          transitionalBidState = BID_STATES.ISSUER_REJECTS_INV_ACCEPT_CBC;
        } else if (invs.state_value === BID_ACTIONS.ACCEPT_BID) {
          transitionalBidState = BID_STATES.INVESTOR_ACCEPTS_AFTER_ISSUER_COUNTER;
        } else if (invs.state_value === BID_ACTIONS.REJECT_BID) {
          transitionalBidState = BID_STATES.INV_REJECTS_ISSUER_COUNTER;
        }
        break;
      case BID_STATES.ISSUER_AFTER_ISSUER_ACCEPTS_BID:
        if (isss.state_value === BID_ACTIONS.ACCEPT_NEGOTIATION) {
          transitionalBidState = BID_STATES.ISSUER_ACCEPTS_AFTER_INVESTOR_ACCEPT;
        } else if (isss.state_value === BID_ACTIONS.ACCEPT_BID_WITH_CBC &&
          invs.state_value === BID_ACTIONS.ACCEPT_REEVAL_CBC) {
          transitionalBidState = BID_STATES.INVESTOR_ACCEPTS_ON_ISSUER_ACCEPT_CBC;
        } else if (isss.state_value === BID_ACTIONS.ACCEPT_NEGOTIATION_WITH_CBC &&
          invs.state_value === BID_ACTIONS.ACCEPT_REEVAL_CBC) {
          transitionalBidState = BID_STATES.INVESTOR_ACCEPTS_ON_ISSUER_ACCEPT_CBC;
        }
        break;
      case BID_STATES.ISSUER_AFTER_INVESTOR_SENT_BID:
        if (isss.state_value === BID_ACTIONS.COUNTER_NEGOTIATION &&
          invs.state_value === BID_ACTIONS.ACCEPT_NEGOTIATION) {
          transitionalBidState = BID_STATES.ISSUER_AFTER_BOTH_COUNTER_INV_ACCEPT;
        } else if (isss.state_value === BID_ACTIONS.REJECT_BID &&
          invs.state_value === BID_ACTIONS.COUNTER_NEGOTIATION) {
          transitionalBidState = BID_STATES.ISSUER_REJECTS_AFTER_BOTH_COUNTER;
        } else if (isss.state_value === BID_ACTIONS.COUNTER_NEGOTIATION &&
          invs.state_value === BID_ACTIONS.COUNTER_NEGOTIATION) {
          transitionalBidState = BID_STATES.INV_COUNTERS_AFTER_BOTH_COUNTER_ISSUER_COUNTERS;
        } else if (isss.state_value === BID_ACTIONS.REJECT_BID &&
          invs.state_value === BID_ACTIONS.ACCEPT_NEGOTIATION) {
          transitionalBidState = BID_STATES.ISSUER_REJECT_BOTH_COUNTER_ISS_COUNTER_INV_A;
        }
        break;
      case BID_STATES.ISSUER_AFTER_INVESTOR_COUNTER:
        if (isss.state_value === BID_ACTIONS.REJECT_NEGOTIATION) {
          transitionalBidState = BID_STATES.ISSUER_REJECT_AFTER_INVESTOR_COUNTER;
        }
        break;
      case BID_STATES.INVESTOR_ON_ISSUER_ACCEPT_CBC:
        if (isss.state_value === BID_ACTIONS.ACCEPT_BID_WITH_CBC &&
          invs.state_value === BID_ACTIONS.REJECT_REEVAL_CBC) {
          transitionalBidState = BID_STATES.INVESTOR_REJECTS_ON_ISSUER_ACCEPT_CBC;
        } else if (isss.state_value === BID_ACTIONS.REJECT_REEVAL_CBC &&
          invs.state_value === BID_ACTIONS.REJECT_REEVAL_CBC) {
          transitionalBidState = BID_STATES.BOTH_REJECT_CBC;
        }
        break;
      case BID_STATES.ISSUER_REJECTED_BID:
        if (isss.state_value === BID_ACTIONS.ACCEPT_NEGOTIATION_WITH_CBC &&
          invs.state_value === BID_ACTIONS.REJECT_REEVAL_CBC) {
          transitionalBidState = BID_STATES.INVESTOR_REJECTS_ON_ISSUER_COUNTER_CBC;
        }
        break;
      default: 
        transitionalBidState = BID_STATES.DEFAULT_STATE || null;
        break;

    }
    return transitionalBidState || baseBidState;
  }

  /**
   * Returns bidding config object for the current user role.
   */
  getConfigForCurrentRole() {
    const isIssuer = this.sharedDataService.getUserDataFromLS().roleType === CpRolesEnum.ISSUER;
    return {
      stateNextActionsMap: isIssuer ? ISSUER_STATE_NEXT_ACTIONS : INVESTOR_STATE_NEXT_ACTIONS,
      actionConfigMap: isIssuer ? ISSUER_NEXT_ACTIONS_CONFIG : INVESTOR_NEXT_ACTIONS_CONFIG,
      currentStateConfigMap: isIssuer ? ISSUER_CURRENT_STATE_CONFIG : INVESTOR_CURRENT_STATE_CONFIG
    };
  }

  /**
   * Generates a formatted maturity date string along with tenure given maturity date and value date.
   **/
  getMaturityDateWithTenure(maturityDateStr, ValueDateStr) {
    const maturityDate = moment(maturityDateStr);
    const valueDate = moment(ValueDateStr);
    const tenure = maturityDate.diff(valueDate.startOf('day'), 'days');
    return {
      maturityDate: maturityDate.format(UI_DATE_FORMAT_MAP.default),
      tenure
    };
  }

  /**
   * Checks and returns the issuer docs in a format the modal understands
   * @param auctionDocs the issuer basic documents related to issuing CP.
   */
  getCompanyDocs(auctionDocs): ViewModalDocFormat[] {
    const docsToView = [];

    if (auctionDocs.board_resolution) {
      docsToView.push({
        title: 'Board Resolution',
        document_url: auctionDocs.board_resolution
      });
    }

    if (auctionDocs.credit_rating_details) {
      if (auctionDocs.credit_rating_details.length > 0) {
        docsToView.push(...auctionDocs.credit_rating_details.map(this.getDocForView));
      } else if (auctionDocs.credit_rating_details &&
        auctionDocs.credit_rating_details.credit_rating_document) {
        docsToView.push(this.getDocForView(auctionDocs.credit_rating_details));
      }
    }
    return docsToView;
  }

  /**
   * Returns credit rating document for view modal
   */
  getDocForView(creditDetails) {
    return {
      title: 'Credit Rating Letter',
      additionalData: `${creditDetails.credit_rating}`,
      document_url: creditDetails.credit_rating_document
    };
  }

}
